// Core
import { Ref } from 'react';
// Packages
import {
  ActivityIcon,
  AlarmClockIcon,
  AlignLeftIcon,
  ArrowDownIcon,
  ArrowDownToLineIcon,
  ArrowLeftIcon,
  ArrowLeftRightIcon,
  ArrowUpIcon,
  ArrowUpToLineIcon,
  BellIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BugIcon,
  Building2Icon,
  CalendarClockIcon,
  CalendarDaysIcon,
  CalendarIcon,
  CalendarOffIcon,
  CalendarXIcon,
  ChartColumnIcon,
  ChartLineIcon,
  ChartPieIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
  ChevronUpIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDotIcon,
  CircleQuestionMarkIcon,
  CircleUserRoundIcon,
  CirclePlusIcon,
  CircleXIcon,
  ClipboardListIcon,
  ClockArrowUpIcon,
  ClockIcon,
  CloudIcon,
  CloudUploadIcon,
  ContactIcon,
  CopyIcon,
  CreditCardIcon,
  DollarSignIcon,
  EllipsisIcon,
  EyeIcon,
  EyeOffIcon,
  FileIcon,
  FileTextIcon,
  FlameIcon,
  GripIcon,
  GripVerticalIcon,
  HeadingIcon,
  HeartIcon,
  HistoryIcon,
  HourglassIcon,
  ImageIcon,
  InboxIcon,
  InfoIcon,
  KeyRoundIcon,
  LanguagesIcon,
  LayersIcon,
  LightbulbIcon,
  LinkIcon,
  ListChecksIcon,
  LockIcon,
  LogOutIcon,
  LucideIcon,
  LucideProps,
  MailIcon,
  MessageCircleWarningIcon,
  MailOpenIcon,
  MapIcon,
  MapPinIcon,
  MapPinOffIcon,
  MessageSquareWarningIcon,
  MonitorIcon,
  MoonIcon,
  NewspaperIcon,
  NotebookPenIcon,
  PaperclipIcon,
  PartyPopperIcon,
  PencilIcon,
  PencilLineIcon,
  PercentIcon,
  PlugIcon,
  PlusIcon,
  PrinterIcon,
  ReceiptIcon,
  ReceiptTextIcon,
  RefreshCwIcon,
  RepeatIcon,
  RotateCcwClockIcon,
  SaveIcon,
  ScanLineIcon,
  SearchIcon,
  SettingsIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  SignatureIcon,
  SirenIcon,
  SlidersHorizontalIcon,
  SquareArrowDownIcon,
  SquareArrowRightIcon,
  SquareArrowUpIcon,
  SquareCheckIcon,
  SquareChevronDownIcon,
  SquareIcon,
  SquareParkingIcon,
  StarIcon,
  SunIcon,
  TagsIcon,
  TextCursorInputIcon,
  TimerOffIcon,
  TrashIcon,
  UnlinkIcon,
  UploadIcon,
  UserCogIcon,
  UserIcon,
  UserXIcon,
  UserPlusIcon,
  UsersIcon,
  UsersRoundIcon,
  WalletIcon,
  WrenchIcon,
  XIcon,
} from 'lucide-react';
import cx from 'classnames';
// Styles
import styles from './icon.module.scss';

// Keys are Lucide names, the same names the mobile app's Icon takes (lucide-react-native, same
// version), so web and mobile share one vocabulary. Named imports from the package root: webpack
// drops every icon not listed here from the production bundle (lucide-react is sideEffects-free and
// has no typed per-icon entry).
const defineIcons = <T extends Record<string, LucideIcon>>(icons: T) => icons;

const ICONS = defineIcons({
  activity: ActivityIcon,
  'alarm-clock': AlarmClockIcon,
  'align-left': AlignLeftIcon,
  'arrow-down': ArrowDownIcon,
  'arrow-down-to-line': ArrowDownToLineIcon,
  'arrow-left': ArrowLeftIcon,
  'arrow-left-right': ArrowLeftRightIcon,
  'arrow-up': ArrowUpIcon,
  'arrow-up-to-line': ArrowUpToLineIcon,
  bell: BellIcon,
  'book-open': BookOpenIcon,
  briefcase: BriefcaseIcon,
  bug: BugIcon,
  'building-2': Building2Icon,
  calendar: CalendarIcon,
  'calendar-clock': CalendarClockIcon,
  'calendar-days': CalendarDaysIcon,
  'calendar-off': CalendarOffIcon,
  'calendar-x': CalendarXIcon,
  'chart-column': ChartColumnIcon,
  'chart-line': ChartLineIcon,
  'chart-pie': ChartPieIcon,
  check: CheckIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-left': ChevronLeftIcon,
  'chevron-right': ChevronRightIcon,
  'chevron-up': ChevronUpIcon,
  'chevrons-up-down': ChevronsUpDownIcon,
  'circle-alert': CircleAlertIcon,
  'circle-check': CircleCheckIcon,
  'circle-dot': CircleDotIcon,
  'circle-question-mark': CircleQuestionMarkIcon,
  'circle-user-round': CircleUserRoundIcon,
  'circle-plus': CirclePlusIcon,
  'circle-x': CircleXIcon,
  'clipboard-list': ClipboardListIcon,
  clock: ClockIcon,
  'clock-arrow-up': ClockArrowUpIcon,
  cloud: CloudIcon,
  'cloud-upload': CloudUploadIcon,
  contact: ContactIcon,
  copy: CopyIcon,
  'credit-card': CreditCardIcon,
  'dollar-sign': DollarSignIcon,
  ellipsis: EllipsisIcon,
  eye: EyeIcon,
  'eye-off': EyeOffIcon,
  file: FileIcon,
  'file-text': FileTextIcon,
  flame: FlameIcon,
  grip: GripIcon,
  'grip-vertical': GripVerticalIcon,
  heading: HeadingIcon,
  heart: HeartIcon,
  history: HistoryIcon,
  hourglass: HourglassIcon,
  image: ImageIcon,
  inbox: InboxIcon,
  info: InfoIcon,
  'key-round': KeyRoundIcon,
  languages: LanguagesIcon,
  layers: LayersIcon,
  lightbulb: LightbulbIcon,
  link: LinkIcon,
  'list-checks': ListChecksIcon,
  lock: LockIcon,
  'log-out': LogOutIcon,
  mail: MailIcon,
  'mail-open': MailOpenIcon,
  map: MapIcon,
  'map-pin': MapPinIcon,
  'map-pin-off': MapPinOffIcon,
  'message-circle-warning': MessageCircleWarningIcon,
  'message-square-warning': MessageSquareWarningIcon,
  monitor: MonitorIcon,
  moon: MoonIcon,
  newspaper: NewspaperIcon,
  'notebook-pen': NotebookPenIcon,
  paperclip: PaperclipIcon,
  'party-popper': PartyPopperIcon,
  pencil: PencilIcon,
  'pencil-line': PencilLineIcon,
  percent: PercentIcon,
  plug: PlugIcon,
  plus: PlusIcon,
  printer: PrinterIcon,
  receipt: ReceiptIcon,
  'receipt-text': ReceiptTextIcon,
  'refresh-cw': RefreshCwIcon,
  repeat: RepeatIcon,
  'rotate-ccw-clock': RotateCcwClockIcon,
  save: SaveIcon,
  'scan-line': ScanLineIcon,
  search: SearchIcon,
  settings: SettingsIcon,
  'shield-alert': ShieldAlertIcon,
  'shield-check': ShieldCheckIcon,
  signature: SignatureIcon,
  siren: SirenIcon,
  'sliders-horizontal': SlidersHorizontalIcon,
  'square-arrow-down': SquareArrowDownIcon,
  'square-arrow-right': SquareArrowRightIcon,
  'square-arrow-up': SquareArrowUpIcon,
  'square-check': SquareCheckIcon,
  'square-chevron-down': SquareChevronDownIcon,
  square: SquareIcon,
  'square-parking': SquareParkingIcon,
  star: StarIcon,
  sun: SunIcon,
  tags: TagsIcon,
  'text-cursor-input': TextCursorInputIcon,
  'timer-off': TimerOffIcon,
  trash: TrashIcon,
  unlink: UnlinkIcon,
  upload: UploadIcon,
  user: UserIcon,
  'user-cog': UserCogIcon,
  'user-x': UserXIcon,
  'user-plus': UserPlusIcon,
  users: UsersIcon,
  'users-round': UsersRoundIcon,
  wallet: WalletIcon,
  wrench: WrenchIcon,
  x: XIcon,
});

export type IconName = keyof typeof ICONS;

export const ICON_SIZE = { sm: 16, md: 20, lg: 24, xl: 40 } as const;

// The glyph inside a badge or a tag, a notch under `sm` so it sits with the text.
export const BADGE_ICON_SIZE = 14;

type TIconSize = keyof typeof ICON_SIZE | number | '1em';

// Lucide's default, also the mobile one, so a glyph has the same weight on both platforms.
const ICON_STROKE_WIDTH = 2;
// Strokes scale with the glyph: 2 at 40px would draw a 3.3px line; big empty-state glyphs go thinner.
const LARGE_ICON_MIN_SIZE = 32;
const ICON_STROKE_WIDTH_LARGE = 1.5;

// A colour other than the surrounding text's, by token. It is set as CSS `color` (the glyph keeps
// stroke="currentColor"): Lucide writes its `color` prop into the SVG stroke attribute, where a
// `var()` is not reliably resolved (WebKit) and no stylesheet rule (hover, a button's own colour)
// can override it. So there is no `color` prop.
const TONE_CLASS = {
  text: styles.text,
  textMuted: styles.textMuted,
  textPlaceholder: styles.textPlaceholder,
  onPrimary: styles.onPrimary,
  primaryHover: styles.primaryHover,
  success: styles.success,
  danger: styles.danger,
} as const;

type TIconTone = keyof typeof TONE_CLASS;

interface IIconProps extends Omit<LucideProps, 'size' | 'ref' | 'color'> {
  name: IconName;
  size?: TIconSize;
  tone?: TIconTone;
  dataCy?: string;
  ref?: Ref<SVGSVGElement>;
}

const getPixelSize = (size: TIconSize) =>
  typeof size === 'string' && size in ICON_SIZE
    ? ICON_SIZE[size as keyof typeof ICON_SIZE]
    : size;

const getDefaultStrokeWidth = (pixelSize: number | string) =>
  typeof pixelSize === 'number' && pixelSize >= LARGE_ICON_MIN_SIZE
    ? ICON_STROKE_WIDTH_LARGE
    : ICON_STROKE_WIDTH;

/**
 * A Lucide glyph by name.
 *
 * @description Colour follows the surrounding text (currentColor) unless a `tone` is passed. The
 * default size is 1em, so a glyph inside text, buttons and menus keeps the box the old react-icons
 * and antd icons had; `sm | md | lg | xl` are the shared 16 / 20 / 24 / 40 px steps.
 * Other props and the ref reach the <svg>: a Tooltip / Popover anchors to it and listens on it.
 *
 * @param name - Lucide icon name (kebab case, as in the mobile app)
 * @param size - sm 16 | md 20 | lg 24 | xl 40 | px number | '1em' (default)
 * @param tone - A colour token instead of the surrounding text colour
 * @param strokeWidth - 2 by default, 1.5 from 32px up
 * @param dataCy - Param for testing purpose
 */
const Icon = ({
  name,
  size = '1em',
  strokeWidth,
  tone,
  className,
  dataCy,
  ref,
  ...svgProps
}: IIconProps) => {
  const Glyph = ICONS[name];
  const pixelSize = getPixelSize(size);

  return (
    <Glyph
      ref={ref}
      size={pixelSize}
      strokeWidth={strokeWidth ?? getDefaultStrokeWidth(pixelSize)}
      className={cx(tone && TONE_CLASS[tone], className)}
      data-cy={dataCy}
      {...svgProps}
    />
  );
};

export default Icon;
